import React, { useState, useEffect } from "react";
import { Dialog, useMediaQuery, useTheme } from "@material-ui/core";
import classNames from "classnames";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import { object, number, string, date } from "yup";

import { convertToInputDate } from "../../services/helpers";

const inputClass =
  "appearance-none block w-full font-display border border-gray-400 rounded py-3 px-4 mb-3 leading-tight focus:outline-none bg-white focus:border-black";

const inputErrorClass = "border border-red-500 b-2 text-red-500";
const labelClass =
  "block tracking-wide text-black font-display text-sm leading-5 mb-2";

const validationSchema = object().shape({
  _id: string().required(),
  quantity: number("Quantity should be a number.").required(
    "Number of Units is required."
  ),
  rate: number("Price should be a number.").required(
    "Price / Unit is required."
  ),
  startDate: date().required("Start Date is required."),
  endDate: date("End Date should be a number.").required(
    "End Date is required."
  ),
});

const EditAdditionalServiceModal = ({
  onSubmit,
  onCancel,
  service,
  modalProps,
  handleTerminationClick,
}) => {
  const { errors, handleSubmit, register, reset } = useForm({
    resolver: yupResolver(validationSchema),
  });
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [terminationModal, setTerminationModal] = useState(false);

  useEffect(() => {
    if (service) {
      reset({
        _id: service._id,
        rate: service.rate,
        startDate: convertToInputDate(service.startDate),
        endDate: convertToInputDate(service.endDate),
        quantity: service.quantity,
      });
    }
  }, [service, reset]);

  return (
    <>
      <Dialog
        // classes={{ paper: { maxHeight: "100%" } }}
        fullScreen={fullScreen}
        {...modalProps}
      >
        <div className="rounded-lg px-8 py-12 w-full h-screen">
          {/* Modal Header */}
          <div className="flex justify-between items-end">
            <div className="space-y-2">
              <p className="uppercase text-xs leading-3 tracking-widest text-gray-600 font-bold">
                Additional Services
              </p>
              <h3 className="text-4xl leading-10 font-bold">{service.name}</h3>
            </div>
            <div className="justify-end -mx-8">
              <button
                type="button"
                onClick={() => setTerminationModal(true)}
                className="uppercase rounded p-2 shadow border tracking-widest font-bold text-sm text-red-600 outline-none"
              >
                terminate service
              </button>
            </div>
          </div>
          {/* Main Form Section */}
          <div className="">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="bg-formBackground -mx-8">
                <div className="px-8 py-4">
                  <div className="sm:w-4/5 w-full mb-6">
                    <label className={labelClass} htmlFor="quantity">
                      Number of Units
                    </label>
                    <input
                      className={classNames({
                        [inputClass]: true,
                        [inputErrorClass]:
                          errors.quantity && errors.quantity.message,
                      })}
                      id="quantity"
                      type="number"
                      placeholder="Number of Units"
                      name="quantity"
                      ref={register}
                    />
                    {errors.quantity && errors.quantity.message && (
                      <div className="text-xs text-red-500 max-w-xs">
                        <span>{errors.quantity.message}</span>
                      </div>
                    )}
                  </div>
                  <div className="sm:w-4/5 w-full mb-6">
                    <label className={labelClass} htmlFor="price">
                      Price / Unit
                    </label>
                    <input
                      className={classNames({
                        [inputClass]: true,
                        [inputErrorClass]: errors.price && errors.price.message,
                      })}
                      id="price"
                      type="number"
                      placeholder="Rs. Amount"
                      name="rate"
                      ref={register}
                    />
                    {errors.price && errors.price.message && (
                      <div className="text-xs text-red-500 max-w-xs">
                        <span>{errors.price.message}</span>
                      </div>
                    )}
                  </div>

                  <div className="sm:w-4/5 w-full mb-6">
                    <label className={labelClass} htmlFor="price">
                      Service Start Date
                    </label>
                    <input
                      className={classNames({
                        [inputClass]: true,
                        [inputErrorClass]:
                          errors.startDate && errors.startDate.message,
                        "bg-gray-200": true,
                      })}
                      id="startDate"
                      type="date"
                      placeholder="Enter Start Date"
                      name="startDate"
                      readOnly
                      ref={register}
                    />
                    {errors.startDate && errors.startDate.message && (
                      <div className="text-xs text-red-500 max-w-xs">
                        <span>{errors.startDate.message}</span>
                      </div>
                    )}
                  </div>

                  <div className="sm:w-4/5 w-full mb-6">
                    <label className={labelClass} htmlFor="endDate">
                      End Date of Service
                    </label>
                    <input
                      className={classNames({
                        [inputClass]: true,
                        [inputErrorClass]:
                          errors.endDate && errors.endDate.message,
                      })}
                      id="endDate"
                      type="date"
                      placeholder="In Months"
                      name="endDate"
                      ref={register}
                    />
                    {errors.endDate && errors.endDate.message && (
                      <div className="text-xs text-red-500 max-w-xs">
                        <span>{errors.endDate.message}</span>
                      </div>
                    )}
                  </div>
                  <input type="hidden" name="_id" ref={register} />
                </div>
              </div>
              <hr className="my-2 border-0 border-gray-100" />
              {/* Action Buttons */}
              <div className="flex items-center md:flex-row-reverse flex-col pl-8 py-6">
                <button
                  className="md:ml-4 md:w-auto w-full md:mb-0 mb-4 px-16 shadow bg-themeYellow rounded-lg py-4 uppercase font-bold text-sm tracking-widest leading-4 outline-none"
                  type="submit"
                >
                  Save
                </button>
                <button
                  type="button"
                  className="bg-white md:w-auto w-full px-16 shadow rounded-lg py-4 uppercase font-bold text-sm tracking-widest leading-4 outline-none"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
      <ConfirmationModal
        onCancel={() => setTerminationModal((val) => !val)}
        onSubmit={() => handleTerminationClick(service)}
        title=" You're about to terminate a service."
        description={`${service.name} services for the membership will be suspended. You will NOT be able to undo this action`}
        mode="terminate"
        modalProps={{ open: terminationModal }}
      />
    </>
  );
};

export default EditAdditionalServiceModal;
